import { HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { GetStatisticInput } from '../models/configurators-2d/statistic/get-statistic-input';
import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { StatisticTypeEnum } from '../enum/statistic-type.enum';
import { BaseService } from './base-service';
import { TunnelDataStore } from '../stores/tunnel/tunnel-data.store';
import { Subscription } from 'rxjs';
import { HBMConfigurationData } from '../stores/tunnel/model/configuration-data/hbm-configuration-data';
import { CONFIG_CONTEXT } from '../enum/config-context.enum';
import { logError } from './helpers/sentry.helper';
import { ComplementaryProductStatistic } from 'src/app/models/statistic/complementary-product-statistic';
import { BathroomAdditionnalStatistics } from '../models/statistic/bathroom-additionnal-statistics';

/** Ligne de statistiques de création de projets Pose : total national et répartition par magasin. */
export interface PoseProjectCreationStatisticRow {
  /** Sous-secteur ou identifiant de configurateur. */
  label: string;
  /** Nombre de créations au national. */
  total: number;
  /** Nombre de créations par code SAP de magasin ; les magasins sans création sont absents. */
  countByStore: Record<string, number>;
}

/** Magasin présenté en colonne des statistiques de création de projets Pose. */
export interface PoseProjectCreationStatisticStore {
  /** Code SAP du magasin. */
  storeId: string;
  /** Nom du magasin. */
  name: string;
}

/** Statistiques de création de projets Pose sur une période, au national et par magasin. */
export interface PoseProjectCreationStatistics {
  /** Magasins, triés par nom. */
  stores: PoseProjectCreationStatisticStore[];
  /** Créations depuis la page d'accueil, une ligne par sous-secteur. */
  homeBySubSector: PoseProjectCreationStatisticRow[];
  /** Créations depuis l'onglet Validation, une ligne par configurateur. */
  validationByConfigurator: PoseProjectCreationStatisticRow[];
}

@Injectable({
  providedIn: 'root',
})
export class StatisticService extends BaseService implements OnInit, OnDestroy {
  private readonly customerStoreId = '1515';

  private subscribe: Subscription;
  private configuration: HBMConfigurationData;

  public get baseApiUrl() {
    return environment.configurators2d.baseApi;
  }

  public get apiUrl() {
    return this.baseApiUrl + 'Statistics/';
  }

  constructor(public tunnelDataStore: TunnelDataStore) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToObservable();
  }

  ngOnDestroy(): void {
    this.subscribe?.unsubscribe();
  }

  private subscribeToObservable() {
    this.subscribe = this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
      if (configurationData) {
        this.configuration = configurationData.get(this.customer.context as CONFIG_CONTEXT) as HBMConfigurationData;
      }
    });
  }

  public async sendStatistic(
    statisticType: StatisticTypeEnum,
    value: number,
    distribId: string,
    uuid?: string
  ): Promise<any> {
    try {
      let input = {
        statisticType: statisticType,
        value: value,
        storeId: this.getCurrentStoreId(),
        projectId: uuid || this?.configuration?.configurationId,
        applicationDistribId: distribId,
      };

      const params = this.buildHttpParamsFromObject(input);

      return await this.post(this.apiUrl + 'SendStatistic', {}, params);
    } catch (error) {
      logError("Erreur lors de l'envoi des statistiques", error);
    }
  }

  public getStatisticDaily(input: GetStatisticInput): Promise<any> {
    let params = new HttpParams()
      // .set('statisticType', input.statisticType.join(', '))
      .set('beginDate', input.beginDate?.toISOString())
      .set('endDate', input.endDate?.toISOString())
      .set('applicationDistribId', input.applicationDistribId);
    params = this.addOptionalStatisticParams(params, input);
    return this.get(this.apiUrl + 'StatisticDaily', params);
  }

  /** Récupère les créations de projets Pose de la période, au national et par magasin. */
  public async getPoseProjectCreationStatistic(input: GetStatisticInput): Promise<PoseProjectCreationStatistics> {
    const params = new HttpParams()
      .set('beginDate', input.beginDate?.toISOString())
      .set('endDate', input.endDate?.toISOString());
    return this.get(this.baseApiUrl + 'pose/statistics', params);
  }

  public async getComplementaryProductStatistic(input: GetStatisticInput): Promise<ComplementaryProductStatistic[]> {
    let params = new HttpParams()
      .set('beginDate', input.beginDate?.toISOString())
      .set('endDate', input.endDate?.toISOString());
    params = this.addOptionalStatisticParams(params, input);
    return this.get(this.apiUrl + 'ComplementaryProductStatistic', params);
  }

  public async getBathroomAdditionnalStatistics(input: GetStatisticInput): Promise<BathroomAdditionnalStatistics> {
    let params = new HttpParams()
      .set('beginDate', input.beginDate?.toISOString())
      .set('endDate', input.endDate?.toISOString());
    params = this.addOptionalStatisticParams(params, input);
    return this.get(this.apiUrl + 'BathroomAdditionnalStatistics', params);
  }

  private getCurrentStoreId(): string {
    const storeId = this.current?.vendor?.store?.SAPStoreId?.trim();
    return storeId || this.customerStoreId;
  }

  private addOptionalStatisticParams(params: HttpParams, input: GetStatisticInput): HttpParams {
    let result = params;
    if (input.storeId?.trim()) {
      result = result.set('storeId', input.storeId.trim());
    }
    if (input.audience) {
      result = result.set('audience', input.audience);
    }
    return result;
  }
}
